/**
 * /wallet — 钱包页
 *
 * 设计稿: _design_reference/mini-wallet.html
 *
 * 功能:
 * 1. Balance hero — 渐变背景 + 装饰圆 + 42px 大字 + ≈¥CNY 副显 + "永久有效"
 * 2. 三列余额拆分(充值余额/奖励金/退款) — 后端 UserInfo 无此拆分字段,诚实隐藏
 * 3. 4格 Quick Actions (充值 / 提现 / 优惠券 / 邀友)
 * 4. 快速充值金额卡 (1000₽/5000₽/10k₽) → useRechargeBalance → MiniCashier
 * 5. 优惠券区块 — 后端无 coupon list endpoint,诚实隐藏
 * 6. 最近账单内联摘要 (useBillList · 最近5条)
 *
 * header 右侧: 账单快捷链接 (设计稿右上角有)
 */
import { useBillList, useRechargeBalance, useUserInfo } from "@kupikupi/api";
import { API_BASE_URL } from "@kupikupi/brand";
import { formatPrice, useExchangeRate } from "@kupikupi/money";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MiniCashier } from "../components/pay/MiniCashier";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuth, useAuthToken } from "../lib/auth";
import { miniDisplayCurrency, miniDisplayLocale } from "../lib/locale-currency";

// ---------------------------------------------------------------------------
// Quick topup presets (design: 1000/5000/10000 ₽ with bonus %)
// ---------------------------------------------------------------------------
interface TopupPreset {
  /** amount in RUB (what the backend rechargeAmount expects) */
  amountRub: number;
  label: string;
  bonusLabel: string;
  bonusPct: string;
  featured: boolean;
}

const TOPUP_PRESETS: TopupPreset[] = [
  { amountRub: 1000, label: "1 000 ₽", bonusLabel: "20 ₽", bonusPct: "+2%", featured: false },
  { amountRub: 5000, label: "5 000 ₽", bonusLabel: "200 ₽", bonusPct: "+4%", featured: true },
  { amountRub: 10000, label: "10k ₽", bonusLabel: "500 ₽", bonusPct: "+5%", featured: false },
];

// ---------------------------------------------------------------------------
// BillRow sub-component
// ---------------------------------------------------------------------------
function BillRow({
  record,
  displayLocale,
  displayCurrency,
}: {
  record: {
    id: string;
    remark: string;
    type: string;
    changeRubKopecks: number;
    changeTime: string;
  };
  displayLocale: "ru-RU" | "zh-CN";
  displayCurrency: "RUB" | "CNY";
}) {
  const isPositive = record.changeRubKopecks > 0;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 0",
        borderBottom: "1px solid var(--color-border)",
      }}
    >
      <div>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{record.remark || record.type}</div>
        {record.changeTime && (
          <div
            style={{
              fontSize: 10,
              color: "var(--color-text-muted)",
              fontFamily: "monospace",
              marginTop: 2,
            }}
          >
            {record.changeTime}
          </div>
        )}
      </div>
      <div
        style={{
          fontWeight: 800,
          fontSize: 14,
          color: isPositive ? "var(--color-primary-fill)" : "var(--color-text)",
          fontFamily: "-apple-system,system-ui,sans-serif",
        }}
      >
        {isPositive ? "+" : ""}
        {formatPrice(record.changeRubKopecks, displayLocale, displayCurrency)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------
export function WalletPage() {
  const { t, i18n } = useTranslation("wallet");
  const rawLocale = miniDisplayLocale(i18n.language);
  // normalizeLocale returns "ru-RU" | "zh-CN"; cast is safe since miniDisplayLocale wraps it
  const displayLocale = rawLocale as "ru-RU" | "zh-CN";
  const displayCurrency = miniDisplayCurrency(i18n.language) as "RUB" | "CNY";

  const { status } = useAuth();
  const authToken = useAuthToken();
  const navigate = useNavigate();

  const { data: user, isLoading: userLoading } = useUserInfo(authToken);
  const bills = useBillList(authToken, { pageNo: 1, pageSize: 5 });
  const recharge = useRechargeBalance(authToken);
  const exchangeRate = useExchangeRate(API_BASE_URL);

  const [selectedPresetIdx, setSelectedPresetIdx] = useState<number | null>(null);
  // After useRechargeBalance resolves → show MiniCashier with this orderNo
  const [rechargeOrderNo, setRechargeOrderNo] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Auth: booting skeleton
  // ---------------------------------------------------------------------------
  if (status === "booting") {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("head.title")} back />
        <div style={{ padding: "0 0 24px" }}>
          <TgSkeleton height={170} radius={0} />
          <div style={{ padding: "16px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
            <TgSkeleton height={96} radius={12} />
            <TgSkeleton height={130} radius={12} />
          </div>
        </div>
      </TgPage>
    );
  }

  // ---------------------------------------------------------------------------
  // Auth: signed-out gate
  // ---------------------------------------------------------------------------
  if (status === "signed-out") {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("head.title")} back />
        <div style={{ padding: 16 }}>
          <TgEmpty
            icon="💳"
            title={t("state.signin_t")}
            hint={t("state.signin_s")}
            cta={
              <button
                type="button"
                onClick={() => navigate({ to: "/login" as never })}
                style={{
                  background: "var(--color-cta)",
                  color: "#fff",
                  border: 0,
                  borderRadius: 10,
                  padding: "10px 24px",
                  fontWeight: 800,
                  fontSize: 14,
                  cursor: "pointer",
                  marginTop: 8,
                }}
              >
                {t("state.signin_cta")}
              </button>
            }
          />
        </div>
      </TgPage>
    );
  }

  // ---------------------------------------------------------------------------
  // Recharge overlay: once we have an orderNo hand off to MiniCashier
  // ---------------------------------------------------------------------------
  if (rechargeOrderNo && authToken) {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("recharge.title")} back={() => setRechargeOrderNo(null)} />
        <MiniCashier orderNo={rechargeOrderNo} authToken={authToken} />
      </TgPage>
    );
  }

  // ---------------------------------------------------------------------------
  // Balance display values
  // ---------------------------------------------------------------------------
  const primaryKopecks = user?.amount != null ? Math.round(user.amount * 100) : null;

  // Format integer part only (design uses bare number + separate ₽ span)
  const balanceInteger =
    primaryKopecks != null
      ? new Intl.NumberFormat(displayLocale, { maximumFractionDigits: 0 }).format(
          primaryKopecks / 100,
        )
      : "—";

  // CNY shadow: prefer user.amountCny (backend-provided, always CNY)
  const cnyValue =
    user?.amountCny != null
      ? user.amountCny
      : exchangeRate.data && primaryKopecks != null
        ? primaryKopecks / 100 / exchangeRate.data.rubPerYuan
        : null;
  const cnyShadow = cnyValue != null
    ? `≈ ¥ ${new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 0 }).format(cnyValue)}`
    : null;

  // ---------------------------------------------------------------------------
  // Topup handler: call mutation → get orderNo → open MiniCashier
  // ---------------------------------------------------------------------------
  async function handleTopup(preset: TopupPreset, idx: number) {
    setSelectedPresetIdx(idx);
    try {
      const orderNo = await recharge.mutateAsync({ rechargeAmount: preset.amountRub });
      if (orderNo) {
        setRechargeOrderNo(orderNo);
      }
    } catch {
      // error surfaced via recharge.isError below
    } finally {
      setSelectedPresetIdx(null);
    }
  }

  const billRecords = bills.data?.records ?? [];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <TgPage tabbar={false}>
      {/* Header: back ← title | 账单 right (design spec) */}
      <TgHeader
        title={t("head.title")}
        back
        right={
          <Link
            to={"/bill" as never}
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "var(--color-primary-fill)",
              textDecoration: "none",
              padding: "4px 0",
            }}
          >
            {t("nav.bill")}
          </Link>
        }
      />

      {/* ── Balance hero (full-bleed, no border-radius per design) ────────── */}
      <div
        style={{
          background: "linear-gradient(135deg,var(--color-primary),var(--color-primary-fill))",
          color: "#fff",
          padding: "24px 18px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circle — design: top:-30 right:-30, 140×140, rgba yellow 0.2 */}
        <div
          style={{
            position: "absolute",
            top: -30,
            right: -30,
            width: 140,
            height: 140,
            background: "rgba(255,210,63,.2)",
            borderRadius: "50%",
          }}
        />

        <div style={{ position: "relative", zIndex: 1 }}>
          {/* Monospace "kupikupi 钱包" label */}
          <div
            style={{
              fontFamily: "monospace",
              fontSize: 10,
              letterSpacing: "0.08em",
              opacity: 0.75,
              textTransform: "uppercase",
            }}
          >
            kupikupi {t("head.title")}
          </div>

          {/* 42px balance number + ₽ */}
          {userLoading ? (
            <div style={{ marginTop: 6 }}>
              <TgSkeleton height={42} width={140} radius={6} />
            </div>
          ) : (
            <div
              style={{
                fontFamily: "-apple-system,system-ui,sans-serif",
                fontSize: 42,
                fontWeight: 900,
                marginTop: 6,
                lineHeight: 1,
                letterSpacing: "-0.02em",
              }}
            >
              {balanceInteger}
              <span style={{ fontSize: 18, opacity: 0.85, marginLeft: 4 }}>₽</span>
            </div>
          )}

          {/* CNY shadow + "永久有效" */}
          {cnyShadow && (
            <div
              style={{
                fontSize: 11,
                opacity: 0.85,
                marginTop: 6,
                fontFamily: "monospace",
              }}
            >
              {cnyShadow} {t("balance.permanent")}
            </div>
          )}

          {/*
            三列余额拆分 (充值余额 / 奖励金yellow / 退款):
            设计稿底部有此行, 但后端 GET /user/info 只返回合计 amount,
            无充值/奖励/退款分项字段。后端补字段前此区块隐藏。
            (acknowledged in audit api_gaps)
          */}
        </div>
      </div>

      {/* ── Scrollable body ────────────────────────────────────────────────── */}
      <div className="tg-body" style={{ flex: 1 }}>
        {/* ── 4-grid Quick Actions ───────────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6 }}>
          {/*充值 — CTA gradient, taps scroll to quick-topup */}
          <button
            type="button"
            disabled={recharge.isPending}
            onClick={() => {
              const el = document.getElementById("wallet-quick-topup");
              el?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            style={{
              background: "linear-gradient(135deg,var(--color-cta),var(--color-cta-hover))",
              color: "#fff",
              borderRadius: 12,
              padding: "14px 4px",
              textAlign: "center",
              border: 0,
              cursor: "pointer",
            }}
          >
            <div style={{ fontSize: 22 }}>+</div>
            <div style={{ fontSize: 10, fontWeight: 800, marginTop: 4 }}>
              {t("actions.topup")}
            </div>
          </button>

          {/* 提现 */}
          <Link
            to={"/withdraw" as never}
            style={{
              background: "#fff",
              border: "1px solid var(--color-border)",
              borderRadius: 12,
              padding: "14px 4px",
              textAlign: "center",
              textDecoration: "none",
              color: "inherit",
              display: "block",
            }}
          >
            <div style={{ fontSize: 22 }}>↗</div>
            <div style={{ fontSize: 10, fontWeight: 800, marginTop: 4 }}>
              {t("actions.withdraw")}
            </div>
          </Link>

          {/* 优惠券 — 后端无 coupon list API; 链接到账单页占位 */}
          <Link
            to={"/bill" as never}
            style={{
              background: "#fff",
              border: "1px solid var(--color-border)",
              borderRadius: 12,
              padding: "14px 4px",
              textAlign: "center",
              textDecoration: "none",
              color: "inherit",
              display: "block",
            }}
          >
            <div style={{ fontSize: 22 }}>🎁</div>
            <div style={{ fontSize: 10, fontWeight: 800, marginTop: 4 }}>
              {t("actions.coupon")}
            </div>
          </Link>

          {/* 邀友 */}
          <Link
            to={"/invite" as never}
            style={{
              background: "#fff",
              border: "1px solid var(--color-border)",
              borderRadius: 12,
              padding: "14px 4px",
              textAlign: "center",
              textDecoration: "none",
              color: "inherit",
              display: "block",
            }}
          >
            <div style={{ fontSize: 22 }}>👥</div>
            <div style={{ fontSize: 10, fontWeight: 800, marginTop: 4 }}>
              {t("actions.invite")}
            </div>
          </Link>
        </div>

        {/* ── Quick topup card ──────────────────────────────────────────── */}
        <div className="tg-card" id="wallet-quick-topup">
          <h4 className="tg-card__h">{t("quick_topup.title")}</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6 }}>
            {TOPUP_PRESETS.map((preset, idx) => {
              const isFeatured = preset.featured;
              const isThisLoading = recharge.isPending && selectedPresetIdx === idx;
              return (
                <button
                  key={preset.amountRub}
                  type="button"
                  disabled={recharge.isPending}
                  onClick={() => handleTopup(preset, idx)}
                  style={{
                    background: isFeatured
                      ? "linear-gradient(180deg,var(--color-primary-soft),#fff)"
                      : "#fff",
                    border: isFeatured
                      ? "1.5px solid var(--color-primary-fill)"
                      : "1.5px solid var(--color-border)",
                    borderRadius: 10,
                    padding: "12px 8px",
                    textAlign: "center",
                    position: "relative",
                    cursor: recharge.isPending ? "not-allowed" : "pointer",
                    opacity: recharge.isPending && !isThisLoading ? 0.5 : 1,
                  }}
                >
                  <div
                    style={{
                      fontFamily: "-apple-system,system-ui,sans-serif",
                      fontSize: 14,
                      fontWeight: 900,
                      color: isFeatured ? "var(--color-primary-fill)" : "var(--color-text)",
                    }}
                  >
                    {isThisLoading ? "…" : preset.label}
                  </div>
                  <div
                    style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}
                  >
                    + {preset.bonusLabel}
                  </div>
                  {/* Bonus badge — top-right corner (design: position absolute) */}
                  <span
                    style={{
                      position: "absolute",
                      top: -6,
                      right: 6,
                      background: "var(--color-cta)",
                      color: "#fff",
                      fontSize: 8,
                      padding: "1px 5px",
                      borderRadius: 3,
                      fontWeight: 800,
                    }}
                  >
                    {preset.bonusPct}
                  </span>
                </button>
              );
            })}
          </div>
          {recharge.isError && (
            <div
              style={{
                marginTop: 8,
                fontSize: 11,
                color: "var(--color-danger,#e53e3e)",
                textAlign: "center",
              }}
            >
              {t("recharge.error_failed")}
            </div>
          )}
        </div>

        {/*
          优惠券区块 (设计稿: 渐变卡片列表 · 显示面额/类型/到期时间):
          后端无 GET /coupon/list endpoint — 整块隐藏直至 API 就绪。
          (acknowledged in audit api_gaps)
        */}

        {/* ── Recent bill history (inline preview, web pattern) ──────────── */}
        <div className="tg-card">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <h4 className="tg-card__h" style={{ margin: 0 }}>
              {t("history.title")}
            </h4>
            <Link
              to={"/bill" as never}
              style={{
                fontSize: 11,
                color: "var(--color-primary-fill)",
                textDecoration: "none",
                fontWeight: 700,
              }}
            >
              {t("history.see_all")}
            </Link>
          </div>

          {bills.isLoading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[0, 1, 2].map((i) => (
                <TgSkeleton key={i} height={44} radius={6} />
              ))}
            </div>
          )}

          {!bills.isLoading && billRecords.length === 0 && (
            <div
              style={{
                padding: "20px 0",
                textAlign: "center",
                color: "var(--color-text-muted)",
                fontSize: 12,
              }}
            >
              {t("empty.hint")}
            </div>
          )}

          {billRecords.length > 0 && (
            <div>
              {billRecords.map((r) => (
                <BillRow
                  key={r.id}
                  record={r}
                  displayLocale={displayLocale}
                  displayCurrency={displayCurrency}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </TgPage>
  );
}
